import { type Dispatch, type FormEvent, type SetStateAction, useState } from "react";

import { Button, Card, ErrorState, Footer, Input, TextLogo } from "../components/ui";
import { ApiError, type ApiUser, login } from "../lib/api";

interface Props {
  onLoggedIn: (user: ApiUser) => void;
}

interface LoginFormProps {
  email: string;
  password: string;
  errorMsg: string | null;
  submitting: boolean;
  setEmail: Dispatch<SetStateAction<string>>;
  setPassword: Dispatch<SetStateAction<string>>;
  onSubmit: (e: FormEvent<HTMLFormElement>) => void;
}

export function LoginPage({ onLoggedIn }: Props): JSX.Element {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    if (submitting) return;
    setErrorMsg(null);
    setSubmitting(true);
    try {
      const res = await login(email, password);
      // OAuth 同意フロー等から ?next= で来た場合は、ログインで張られた
      // session cookie を持って backend の元 URL (例: /api/oauth/authorize) へ戻す。
      // next は backend ルートのため SPA 内遷移ではなくフルナビゲーションが必要。
      const next = safeNextPath();
      if (next !== null) {
        window.location.href = next;
        return;
      }
      onLoggedIn(res.user);
    } catch (e) {
      setErrorMsg(loginErrorMessage(e));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-bg px-4 py-8">
      <TextLogo variant="stacked" size="xl" />
      <p className="mt-4 text-center text-base leading-7 text-text-secondary">
        不動産業務をシンプルに
      </p>

      <div className="mt-8 w-full max-w-sm">
        <Card elevated className="space-y-6">
          <div>
            <h1 className="text-xl font-semibold text-text-primary">ログイン</h1>
            <p className="mt-1 text-sm text-text-secondary">
              案件、顧客、対応履歴を一つの場所で管理します。
            </p>
          </div>
          <LoginForm
            email={email}
            password={password}
            errorMsg={errorMsg}
            submitting={submitting}
            setEmail={setEmail}
            setPassword={setPassword}
            onSubmit={handleSubmit}
          />

          <p className="text-xs leading-5 text-text-tertiary">
            初回利用時は管理者にアカウント発行を依頼してください。
          </p>
        </Card>
      </div>
      <Footer className="mt-8" />
    </div>
  );
}

function LoginForm(props: LoginFormProps): JSX.Element {
  return (
    <form onSubmit={props.onSubmit} noValidate className="space-y-4">
      <LoginField
        id="email"
        label="メールアドレス"
        type="email"
        value={props.email}
        autoComplete="email"
        onChange={props.setEmail}
      />
      <LoginField
        id="password"
        label="パスワード"
        type="password"
        value={props.password}
        autoComplete="current-password"
        onChange={props.setPassword}
      />
      {props.errorMsg !== null && (
        <ErrorState title="ログインできません" message={props.errorMsg} className="p-4 text-left" />
      )}
      <Button type="submit" fullWidth loading={props.submitting}>
        ログイン
      </Button>
    </form>
  );
}

function LoginField(props: {
  id: "email" | "password";
  label: string;
  type: "email" | "password";
  value: string;
  autoComplete: string;
  onChange: Dispatch<SetStateAction<string>>;
}): JSX.Element {
  return (
    <div>
      <label htmlFor={props.id} className="mb-1 block text-sm font-medium text-text-primary">
        {props.label}
      </label>
      <Input
        id={props.id}
        type={props.type}
        autoComplete={props.autoComplete}
        inputMode={props.type === "email" ? "email" : undefined}
        autoCapitalize={props.type === "email" ? "off" : undefined}
        spellCheck={props.type === "email" ? false : undefined}
        required
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
      />
    </div>
  );
}

/**
 * ?next= の戻り先を返す。open redirect 防止のため同一オリジンの絶対パスのみ許可し、
 * protocol-relative (`//`) や `/\` 始まりは拒否する。
 */
function safeNextPath(): string | null {
  const raw = new URLSearchParams(window.location.search).get("next");
  if (raw === null || raw === "") return null;
  if (!raw.startsWith("/") || raw.startsWith("//") || raw.startsWith("/\\")) return null;
  return raw;
}

function loginErrorMessage(e: unknown): string {
  if (!(e instanceof ApiError)) return "ログインに失敗しました。";
  if (e.status === 401) return "メールアドレスまたはパスワードが違います。";
  if (e.status === 400) return "入力内容を確認してください。";
  if (e.code === "NETWORK_ERROR") return "ネットワークに接続できません。";
  return "ログインに失敗しました。少し待ってからもう一度試してください。";
}
